import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import UserDocumentViewer from "./UserDocumentViewer";

const UserVisaApplications = () => {
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const url = import.meta.env.VITE_URL || "http://localhost:3000";

  useEffect(() => {
    fetchMyVisas();
  }, []);

  const fetchMyVisas = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${url}/api/visa/my-applications`, {
        withCredentials: true,
      });
      if (res.data.success) {
        setVisas(res.data.applications);
      }
    } catch (error) {
      toast.error("Failed to load visa applications");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500 animate-pulse">Loading your visa applications...</div>;
  }

  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        ✈️ My Visa Applications
      </h2>

      {visas.length === 0 ? (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-8 text-center">
          <p className="text-gray-500">You haven't applied for any visas yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visas.map((visa) => (
            <div key={visa._id} className="bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {visa.visaDetails?.country} - {visa.visaDetails?.visaType}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Applied on: {new Date(visa.createdAt).toLocaleString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                    })}
                  </p>
                </div>
                <div>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                    visa.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200' :
                    visa.status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                    'bg-yellow-50 text-yellow-700 border-yellow-200'
                  }`}>
                    {visa.status.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="text-sm bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-200">
                  <div>
                    <p className="text-gray-500 mb-1 text-xs uppercase tracking-wider">Primary Applicant</p>
                    <p className="font-medium text-gray-900">{visa.primaryApplicant?.fullName}</p>
                    <p className="text-gray-600">Passport: {visa.primaryApplicant?.passportNumber}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-2 text-xs uppercase tracking-wider">Uploaded Documents</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(visa.documents || {}).map(([key, value]) => {
                        if (!value) return null;
                        return (
                          <a key={key} href={`${url}/uploads/visaApplications/${value}`} target="_blank" rel="noreferrer"
                             className="text-xs bg-white border border-gray-200 text-blue-600 px-2 py-1 rounded hover:bg-blue-50">
                            {key}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-gray-500 mb-2 text-xs uppercase tracking-wider">Family Members ({visa.familyMembers?.length || 0})</p>
                  {visa.familyMembers?.length > 0 ? (
                    <div className="space-y-3">
                      {visa.familyMembers.map((member, idx) => (
                        <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                          <p className="font-medium text-gray-900">{member.fullName}</p>
                          <p className="text-gray-600 text-xs mb-2">Passport: {member.passportNumber}</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(member.documents || {}).map(([docKey, docVal]) => {
                              if (!docVal) return null;
                              return (
                                <a key={docKey} href={`${url}/uploads/visaApplications/${docVal}`} target="_blank" rel="noreferrer"
                                   className="text-xs bg-gray-50 border border-gray-200 text-blue-600 px-2 py-1 rounded hover:bg-blue-50">
                                  {docKey}
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm italic">No family members added.</p>
                  )}
                </div>
              </div>

              {visa.adminRemark && (
                <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                  <p className="text-sm font-medium text-red-800">Admin Remark:</p>
                  <p className="text-sm text-red-700">{visa.adminRemark}</p>
                </div>
              )}
              
              {visa.eTicketPdfPath && (
                <div className="mt-4 p-3 bg-green-50 border-l-4 border-green-500 rounded-r-lg flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-800">Final Visa / E-Ticket:</p>
                    <p className="text-sm text-green-700">Your visa document has been uploaded by the admin.</p>
                  </div>
                  <a
                    href={`${url}/${visa.eTicketPdfPath}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 transition-colors"
                  >
                    Download
                  </a>
                </div>
              )}

              {visa.adminDocuments && visa.adminDocuments.length > 0 && (
                <div className="mt-4">
                  <UserDocumentViewer adminDocuments={visa.adminDocuments} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default UserVisaApplications;
